<template>
  <div :id="container"></div>
</template>
<script setup>
import { watch, nextTick } from 'vue'
import Highcharts from 'highcharts'

import * as Sentry from '@sentry/vue'
import { formatPercentage } from '@/utils/Utility.ts'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  container: String,
  categories: Array,
  series: Array,
  isWaitingMode: Boolean,
})

const { language } = useSessionValues()
const drawChart = (container, series, categories, isWaitingMode, language) => {
  try {
    if (!container || !series || !categories) return
    // without nextTick, highcharts may use the container before it exists in the DOM, it is important!
    nextTick(() => {
      // if for some reason the container is not yet mounted, go back.
      if (!document.getElementById(props.container)) return

      Highcharts.chart(props.container, {
        title: null,
        chart: {
          alignTicks: true,
          type: 'bar',
          scrollablePlotArea: {
            minWidth: 700,
            scrollPositionX: 1,
          },
          plotBorderWidth: 0,
          style: {
            fontFamily: `SourceSansPro, Sans-Serif, "Sans Serif"`,
          },
        },
        credits: {
          enabled: false,
        },
        series: series,
        xAxis: {
          categories: categories,
          title: {
            text: null,
          },
          gridLineColor: '#E5F2F6',
          lineColor: '#E5F2F6',
          tickColor: '#E5F2F6',
          tickLength: 0,
          gridLineWidth: 1,
          labels: {
            style: { fontSize: 16, color: '#000' },
            align: 'right',
            overflow: 'justify',
            reserveSpace: true,
          },
        },
        yAxis: {
          min: 0,
          max: 100,
          tickInterval: 25,
          gridLineColor: '#E5F2F6',
          type: 'percentage',
          title: false,
          labels: {
            padding: 30,
            formatter: function () {
              return `<p class="tw:text-gray-700 tw:text-xs">
                ${formatPercentage(language.value, this.value, 0, 0)}
                </p >`
            },
          },
        },
        plotOptions: {
          series: {
            color: '#1391D2',
            states: {
              hover: {
                enabled: false,
              },
              inactive: {
                opacity: 1,
              },
            },
          },
          bar: {
            borderRadius: '4px',
            dataLabels: {
              enabled: true,
              align: 'center',
              x: 50,
              y: 0,
              style: {
                fontSize: '24px',
              },
              useHTML: true,
              formatter: function () {
                if (this.y < 1 && this.y !== 0)
                  return `<p class="tw:flex tw:text-blue-800"><${formatPercentage(language.value, 1, 0, 0)}</p>`
                else if (this.y === 0) {
                  return ''
                } else {
                  return `
                  <p class="tw:flex tw:text-blue-800">${
                    isWaitingMode ? '' : formatPercentage(language.value, this.y, 0, 0)
                  }</p>`
                }
              },
            },
            groupPadding: 0.1,
          },
        },
        legend: {
          enabled: false,
        },

        tooltip: {
          enabled: false,
        },
      })
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

watch(
  () => props,
  newProps => {
    if (newProps.series?.length > 0 && newProps.container) {
      drawChart(newProps.container, newProps.series, newProps.categories, props.isWaitingMode, language)
    }
  },
  { deep: true, immediate: true }
)

watch(language, () => {
  if (props.series?.length > 0 && props.container) {
    drawChart(props.container, props.series, props.categories, props.isWaitingMode, language)
  }
})
</script>
